import React, { useState, useEffect } from "react";
import { Box } from "@mui/material";
import TextButtonComponet from "../../../component/atom/Buttons/TextButton";
import GuestRepository from "../../../core/repository/QuizeRepository";
import TextInputComponent from "../../../component/atom/Inputs/TextInput";
import ThumbnailUploadComponent from "../../../component/atom/Inputs/Thumbnail";
import { buildThumbnailUrl, DOCUMENT_STATUS, onToast } from "../../../core/Constant";

const AdminFolderEditModal = ({ folder, baseUrl, onClose, onSuccess, updateDocument }) => {
  const [folderName, setFolderName] = useState("");
  const [thumbnail, setThumbnail] = useState(null);
  const [isThumbnailRemoved, setIsThumbnailRemoved] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const urlToFile = async (url, filename) => {
    const response = await fetch(url);
    const blob = await response.blob();
    const mimeType = blob.type || "application/octet-stream";
    return new File([blob], filename, { type: mimeType });
  };

  useEffect(() => {
    if (folder) {
      if (folder.name || folder.directory_name) {
        setFolderName(folder.name || folder.directory_name);
      }

      const rawThumbnail = folder.thumbnail || folder.image_url || folder.quiz_thumbnail;

      if (rawThumbnail) {
        setThumbnail(buildThumbnailUrl(baseUrl, rawThumbnail));
      }
    }
  }, [folder, baseUrl]);

  const handleThumbnailChange = (file) => {
    setThumbnail(file);
    if (!file) {
      setIsThumbnailRemoved(true);
    } else {
      setIsThumbnailRemoved(false);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!folderName.trim()) {
      setError("Folder name is required");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let response;

      if (updateDocument) {
        const fd = new FormData();
        fd.append("id", folder?.id);
        fd.append("name", folderName.trim());
        fd.append("is_directory", 1);
        if (folder?.course_id) fd.append("course_id", folder.course_id);
        if (folder?.parent_id) fd.append("parent_id", folder.parent_id);

        let thumbnailToSend = thumbnail;
        if (typeof thumbnailToSend === "string" && thumbnailToSend && !isThumbnailRemoved) {
          try {
            thumbnailToSend = await urlToFile(thumbnailToSend, `folder_thumb_${folder?.id}.png`);
          } catch (err) {
            console.error("Failed to convert thumbnail to file:", err);
          }
        }

        if (thumbnailToSend instanceof File || thumbnailToSend instanceof Blob) {
          fd.append("thumbnail", thumbnailToSend);
          fd.append("delete_thumbnail", 0);
        } else {
          fd.append("delete_thumbnail", 1);
        }

        response = await updateDocument({ document_id: folder?.id, fd });
      } else {
        // --- Quiz Module Flow ---
      const payload = {
        id: folder?.id,
        name: folderName.trim(),
        course_id: folder?.course_id ?? null,
        parent_id: folder?.parent_id ?? null,
        delete_thumbnail: isThumbnailRemoved ? DOCUMENT_STATUS.ACTIVE : DOCUMENT_STATUS.INACTIVE,
      };

      // Only send payload.thumbnail if user selected a new File
      if (thumbnail instanceof File) {
        payload.thumbnail = thumbnail;
      }

        response = await GuestRepository.onUpdateQuizDirectory(payload);
      }

      const responseData = response?.data || response;
      const responseMessage = responseData?.message || response?.message || "Folder updated successfully";
      
      const isSuccess =
        responseData?.status_code === 200 ||
        responseData?.status === true ||
        response?.status === 200 ||
        Boolean(responseData?.document) ||
        Boolean(responseData?.quiz_directory);

      if (isSuccess) {
        onToast("Success", {
          message: responseMessage,
          status_code: 1,
        });
        onSuccess();
      } else {
        const failureMsg = responseMessage || "Failed to update folder.";
        setError(failureMsg);
        onToast("Error", {
          message: failureMsg,
          status_code: 0,
        });
      }
    } catch (err) {
      console.error("Error updating folder:", err);
      const errMessage = err?.response?.data?.message || "Something went wrong while updating.";
      setError(errMessage);
      onToast("Error", {
        message: errMessage,
        status_code: 0,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
      <Box sx={{ mb: 2 }}>
        <TextInputComponent
          label="Folder Name"
          placeholder="Enter folder name"
        value={folderName}
        onchange={(e) => {
          setFolderName(e.target.value);
          if (error) setError("");
        }}
          isRequired={true}
          isError={Boolean(error)}
          error={error}
          containerStyle={{ padding: 0 }}
        />
      </Box>

      {/* Folder Thumbnail Component */}
      <Box sx={{ mb: 3 }}>
        <ThumbnailUploadComponent
          value={thumbnail}
          onChange={handleThumbnailChange}
          label="Folder Thumbnail"
          description="PNG, JPG, JPEG up to 2MB"
        />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
        <TextButtonComponet
          text="Cancel"
          classStyle="btn btn-secondary"
          onButtonClick={onClose}
          isDisabled={loading}
        />
        <TextButtonComponet
          text={loading ? "Saving..." : "Save"}
          isDisabled={loading || !folderName.trim()}
          onButtonClick={handleSubmit}
        />
      </Box>
    </Box>
  );
};

export default AdminFolderEditModal;